import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Crown, Copy, Wallet, RotateCcw, Users as UsersIcon, Trophy } from "lucide-react";
import { toast } from "sonner";

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

const TIER_LABELS = {
  Diamond: { emoji: "💎", grad: "linear-gradient(135deg,#22d3ee,#0891b2)" },
  Platinum: { emoji: "🏆", grad: "linear-gradient(135deg,#c4b5fd,#7c3aed)" },
  Gold: { emoji: "🥇", grad: "linear-gradient(135deg,#fcd34d,#d97706)" },
  Silver: { emoji: "🥈", grad: "linear-gradient(135deg,#e2e8f0,#64748b)" },
  Bronze: { emoji: "🥉", grad: "linear-gradient(135deg,#fbbf24,#92400e)" },
};

export default function Referrals() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [customers, setCustomers] = useCached("customers", []);
  const [logs, setLogs] = useCached("member_logs", []);
  const [ranking, setRanking] = useCached("member_ranking", []);

  const load = () => {
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/members/commission-logs").then(r => setLogs(r.data)).catch(() => {});
    api.get("/members/ranking").then(r => setRanking(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const members = customers.filter(c => c.is_member);
  const totalEarned = members.reduce((a, c) => a + (c.commission_earned || 0), 0);
  const referred = customers.filter(c => c.referred_by_code);
  const podium = ranking.slice(0, 3);
  const rest = ranking.slice(3);

  const resetCom = async (id) => {
    if (!window.confirm("Reset komisi member (setelah dibayarkan)?")) return;
    await api.post(`/customers/${id}/reset-commission`);
    toast.success("Komisi direset (sudah dibayarkan)");
    load();
  };

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Member & Referral</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Program Loyalitas</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-neutral-200 shadow-none">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Total Member</div>
            <div className="font-display font-bold text-3xl mt-2 font-mono-tab">{members.length}</div>
            <div className="text-xs text-neutral-500 mt-1">Pelanggan aktif dengan komisi</div>
          </CardContent>
        </Card>
        <Card className="border-neutral-200 shadow-none">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Total Komisi Terkumpul</div>
            <div className="font-display font-bold text-3xl mt-2 font-mono-tab" style={{ color: "var(--brand-primary)" }}>{fmtIDR(totalEarned)}</div>
            <div className="text-xs text-neutral-500 mt-1">Belum dibayarkan ke member</div>
          </CardContent>
        </Card>
        <Card className="border-neutral-200 shadow-none">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Referral Terpakai</div>
            <div className="font-display font-bold text-3xl mt-2 font-mono-tab">{referred.length}</div>
            <div className="text-xs text-neutral-500 mt-1">Transaksi via kode referral</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="ranking">
        <TabsList>
          <TabsTrigger value="ranking" data-testid="tab-ranking"><Trophy className="w-4 h-4 mr-1" /> Ranking</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members"><Crown className="w-4 h-4 mr-1" /> Member</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><Wallet className="w-4 h-4 mr-1" /> Log Komisi</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="tab-refs"><UsersIcon className="w-4 h-4 mr-1" /> Via Referral</TabsTrigger>
        </TabsList>

        <TabsContent value="ranking" className="space-y-6 mt-4">
          {ranking.length === 0 && (
            <div className="text-center py-16 text-neutral-500">Belum ada member. Aktifkan member di halaman Pelanggan.</div>
          )}
          {podium.length > 0 && (
            <div className="grid grid-cols-3 gap-3 md:gap-6 items-end">
              {[podium[1], podium[0], podium[2]].filter(Boolean).map((m) => {
                const isFirst = m.rank === 1;
                const heights = { 1: "md:h-56 h-40", 2: "md:h-44 h-32", 3: "md:h-40 h-28" };
                const t = TIER_LABELS[m.tier] || TIER_LABELS.Bronze;
                return (
                  <div key={m.id} data-testid={`podium-${m.rank}`} className="flex flex-col items-center">
                    <div className="text-4xl md:text-5xl mb-1">{["", "🥇", "🥈", "🥉"][m.rank]}</div>
                    <div className={`w-full ${heights[m.rank]} rounded-t-2xl flex flex-col items-center justify-end p-3 text-white shadow-lg`}
                      style={{ background: t.grad }}>
                      <div className="text-[10px] uppercase tracking-widest opacity-90">{t.emoji} {m.tier}</div>
                      <div className="font-display font-bold text-sm md:text-lg text-center leading-tight mt-1 truncate w-full" title={m.full_name}>{m.full_name}</div>
                      <div className="font-mono-tab text-xs md:text-sm mt-1">{fmtIDR(m.commission_earned)}</div>
                      <div className="text-[10px] opacity-90 mt-0.5">{m.total_referrals} referral · {m.total_transactions} trx</div>
                      {isFirst && <div className="text-[10px] uppercase tracking-widest mt-1 border-t border-white/40 pt-1 w-full text-center">🏆 Juara 1</div>}
                    </div>
                    <div className="w-full text-center py-2 rounded-b-lg font-mono-tab font-bold text-xs md:text-sm bg-neutral-900 text-white">#{m.rank}</div>
                  </div>
                );
              })}
            </div>
          )}

          {rest.length > 0 && (
            <Card className="border-neutral-200 shadow-none">
              <CardContent className="p-0">
                <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold px-4 pt-4">Peringkat Selanjutnya</div>
                <div className="divide-y divide-neutral-100 mt-2">
                  {rest.map(m => {
                    const t = TIER_LABELS[m.tier] || TIER_LABELS.Bronze;
                    return (
                      <div key={m.id} data-testid={`rank-${m.rank}`} className="flex items-center gap-3 md:gap-4 p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center font-display font-bold text-neutral-700 bg-neutral-100 shrink-0">#{m.rank}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-sm truncate">{m.full_name}</div>
                            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white" style={{ background: m.tier_color }}>{t.emoji} {m.tier}</span>
                          </div>
                          <div className="text-xs text-neutral-500 font-mono-tab mt-0.5">{m.referral_code} · {m.total_referrals} referral · {m.total_transactions} transaksi</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono-tab font-bold text-sm md:text-base" style={{ color: "var(--brand-primary)" }}>{fmtIDR(m.commission_earned)}</div>
                          <div className="text-[10px] text-neutral-500">{m.commission_percent}% · {m.points} poin</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">Sistem Tier</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                {Object.entries(TIER_LABELS).map(([name, t]) => (
                  <div key={name} className="rounded-md p-3 text-white" style={{ background: t.grad }}>
                    <div className="font-display font-bold flex items-center gap-1">{t.emoji} {name}</div>
                    <div className="opacity-90 text-[10px] mt-1 font-mono-tab">
                      {name === "Diamond" && "≥ Rp5.000.000"}
                      {name === "Platinum" && "≥ Rp3.000.000"}
                      {name === "Gold" && "≥ Rp1.000.000"}
                      {name === "Silver" && "≥ Rp500.000"}
                      {name === "Bronze" && "< Rp500.000"}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="py-3 px-4">Member</th>
                      <th className="px-4">Kode</th>
                      <th className="px-4">%</th>
                      <th className="px-4 text-right">Komisi Terkumpul</th>
                      <th className="px-4">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(m => (
                      <tr key={m.id} className="border-b border-neutral-100" data-testid={`member-${m.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-red-600" />
                            <div>
                              <div className="font-medium">{m.full_name}</div>
                              <div className="text-xs text-neutral-500 font-mono-tab">{m.whatsapp || "-"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 font-mono-tab font-semibold flex items-center gap-1">{m.referral_code}
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(m.referral_code); toast.success("Disalin"); }}><Copy className="w-3 h-3" /></Button>
                        </td>
                        <td className="px-4 font-mono-tab font-bold" style={{ color: "var(--brand-primary)" }}>{m.commission_percent}%</td>
                        <td className="px-4 text-right font-mono-tab font-bold text-lg" style={{ color: "var(--brand-primary)" }}>{fmtIDR(m.commission_earned || 0)}</td>
                        <td className="px-4">
                          {isOwner && m.commission_earned > 0 &&
                            <Button size="sm" variant="outline" onClick={() => resetCom(m.id)} data-testid={`reset-${m.id}`}><RotateCcw className="w-3 h-3 mr-1" /> Sudah Dibayar</Button>}
                        </td>
                      </tr>
                    ))}
                    {members.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-neutral-500">Belum ada member. Jadikan pelanggan sebagai member di halaman Pelanggan.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="py-3 px-4">Tanggal</th><th className="px-4">Member</th><th className="px-4">Transaksi</th><th className="px-4">Pelanggan</th><th className="px-4 text-right">Komisi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id} className="border-b border-neutral-100">
                        <td className="py-3 px-4 text-xs font-mono-tab text-neutral-500">{new Date(l.created_at).toLocaleString("id-ID")}</td>
                        <td className="px-4"><span className="flex items-center gap-1"><Crown className="w-3 h-3 text-red-600" /> {l.member_name}</span></td>
                        <td className="px-4 font-mono-tab text-xs">{l.rental_code}</td>
                        <td className="px-4">{l.customer_name}</td>
                        <td className="px-4 text-right font-mono-tab font-semibold" style={{ color: "var(--brand-primary)" }}>{fmtIDR(l.amount)} <span className="text-xs text-neutral-500">({l.percent}%)</span></td>
                      </tr>
                    ))}
                    {logs.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-neutral-500">Belum ada catatan komisi</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6">
              <div className="space-y-2">
                {referred.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-neutral-100">
                    <div>
                      <div className="font-medium text-sm">{c.full_name}</div>
                      <div className="text-xs text-neutral-500">Direferensikan oleh: <span className="font-mono-tab">{c.referred_by_code}</span></div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded bg-neutral-100">{c.acquisition_source}</div>
                  </div>
                ))}
                {referred.length === 0 && <div className="text-center text-neutral-500 py-6">Belum ada pelanggan dari referral</div>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
